import { useRef } from 'react';

import Button from 'components/Button';
import type { FacetResponse } from 'models/api';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

interface Props {
	activeFacets: FacetResponse[];
	className?: string;
	disabled?: boolean;
	onClearAllClick: () => void;
	onClearItemClick: (facetId: string, itemText: string) => void;
	/** When keyboard focus is lost from this component due to element removal */
	onFocusLoss: () => void;
}

export default function ProductListActiveFilters({
	activeFacets,
	className,
	disabled,
	onClearAllClick,
	onClearItemClick,
	onFocusLoss,
}: Props) {
	const itemsRef = useRef<(HTMLLIElement | null)[]>([]);
	const { t } = useI18n();

	const activeItems = activeFacets
		.flatMap((facet) =>
			facet.facetItems?.map((item) => ({
				facetId: facet.id,
				facetTitle: facet.title,
				text: item.item,
			})),
		)
		.filter(is.truthy);

	if (activeItems.length === 0) {
		return null;
	}

	const moveFocus = (removedIndex: number) => {
		const adjacentLi =
			itemsRef.current[removedIndex + 1] ?? itemsRef.current[removedIndex - 1];
		adjacentLi?.querySelector('button')?.focus();
	};

	return (
		<ul className={cn('flex flex-wrap gap-4', className)}>
			{activeItems.map(({ facetId, facetTitle, text }, i) => (
				<li
					key={`${facetId}-${text}`}
					ref={(el) => {
						itemsRef.current[i] = el;
					}}
				>
					<Button
						variant="branded"
						size="sm"
						leadingIcon="close"
						aria-label={t('remove_filter_screenreader', {
							item: `${facetTitle} ${text}`,
						})}
						onClick={() => {
							// If it's the last button, the clear button will also disappear
							// so focus can't be moved internally.
							if (activeItems.length === 1) {
								onFocusLoss();
							} else {
								moveFocus(i);
							}
							onClearItemClick(facetId, text);
						}}
						disabled={disabled}
					>
						{text}
					</Button>
				</li>
			))}
			<li className="ml-2">
				<Button
					variant="text"
					onClick={() => {
						onClearAllClick();
						onFocusLoss();
					}}
					disabled={disabled}
					hasExpandedHitArea
				>
					{t('product_list_clear_filters_button')}
				</Button>
			</li>
		</ul>
	);
}
ProductListActiveFilters.displayName = 'ProductListActiveFilters';
